import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  Pagination,
  Grid,
  Switch,
  FormControlLabel,
  Rating,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  outletManagementService,
  AdminPlace,
  PlaceFilterOptions,
} from '../../services/api/outletManagement.service';

interface PlaceFormData {
  title: string;
  url: string;
  address: string;
  city: string;
  state: string;
  country_code: string;
  latitude: string;
  longitude: string;
  category_name: string;
  brand_id: string;
  place_id: string;
  permanently_closed: boolean;
  temporarily_closed: boolean;
}

const emptyForm: PlaceFormData = {
  title: '',
  url: '',
  address: '',
  city: '',
  state: '',
  country_code: 'ID',
  latitude: '',
  longitude: '',
  category_name: '',
  brand_id: '',
  place_id: '',
  permanently_closed: false,
  temporarily_closed: false,
};

const OutletManagementPage: React.FC = () => {
  // Data state
  const [places, setPlaces] = useState<AdminPlace[]>([]);
  const [filterOptions, setFilterOptions] = useState<PlaceFilterOptions>({ cities: [], brands: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedPlace, setSelectedPlace] = useState<AdminPlace | null>(null);

  // Form state
  const [formData, setFormData] = useState<PlaceFormData>({ ...emptyForm });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState<AdminPlace | null>(null);

  // Context menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPlace, setMenuPlace] = useState<AdminPlace | null>(null);

  const isReadOnly = dialogMode === 'view';

  // Load places
  const loadPlaces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await outletManagementService.getPlaces({
        page,
        limit: 10,
        search: searchQuery || undefined,
        brand_id: filterBrand || undefined,
        city: filterCity || undefined,
        status: filterStatus || undefined,
      });
      setPlaces(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      setError('Failed to load places');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filterBrand, filterCity, filterStatus]);

  // Load filter options
  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await outletManagementService.getFilterOptions();
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  useEffect(() => {
    loadPlaces();
  }, [loadPlaces]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Context menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, place: AdminPlace) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuPlace(place);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuPlace(null);
  };

  // Dialog handlers
  const openCreateDialog = () => {
    setDialogMode('create');
    setFormData({ ...emptyForm });
    setSelectedPlace(null);
    setDialogOpen(true);
  };

  const openEditDialog = (place: AdminPlace) => {
    setDialogMode('edit');
    setSelectedPlace(place);
    setFormData({
      title: place.title || '',
      url: place.url || '',
      address: place.address || '',
      city: place.city || '',
      state: place.state || '',
      country_code: place.country_code || 'ID',
      latitude: place.latitude != null ? String(place.latitude) : '',
      longitude: place.longitude != null ? String(place.longitude) : '',
      category_name: place.category_name || '',
      brand_id: place.brand_id || '',
      place_id: place.place_id || '',
      permanently_closed: place.permanently_closed || false,
      temporarily_closed: place.temporarily_closed || false,
    });
    setDialogOpen(true);
  };

  const openViewDialog = (place: AdminPlace) => {
    setDialogMode('view');
    setSelectedPlace(place);
    setFormData({
      title: place.title || '',
      url: place.url || '',
      address: place.address || '',
      city: place.city || '',
      state: place.state || '',
      country_code: place.country_code || 'ID',
      latitude: place.latitude != null ? String(place.latitude) : '',
      longitude: place.longitude != null ? String(place.longitude) : '',
      category_name: place.category_name || '',
      brand_id: place.brand_id || '',
      place_id: place.place_id || '',
      permanently_closed: place.permanently_closed || false,
      temporarily_closed: place.temporarily_closed || false,
    });
    setDialogOpen(true);
  };

  // Save handler
  const handleSave = async () => {
    if (!formData.title) {
      setError('Title is required');
      return;
    }
    if (!formData.brand_id) {
      setError('Brand is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        url: formData.url || undefined,
        latitude: formData.latitude ? Number(formData.latitude) : undefined,
        longitude: formData.longitude ? Number(formData.longitude) : undefined,
      };

      if (dialogMode === 'create') {
        await outletManagementService.createPlace(payload);
        setSuccessMessage('Place created successfully');
      } else if (dialogMode === 'edit' && selectedPlace) {
        await outletManagementService.updatePlace(selectedPlace.id, payload);
        setSuccessMessage('Place updated successfully');
      }
      setDialogOpen(false);
      loadPlaces();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to save place';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!placeToDelete) return;

    setLoading(true);
    setError(null);
    try {
      await outletManagementService.deletePlace(placeToDelete.id);
      setSuccessMessage('Place deleted successfully');
      setDeleteDialogOpen(false);
      setPlaceToDelete(null);
      loadPlaces();
    } catch (err) {
      setError('Failed to delete place');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (place: AdminPlace) => {
    if (place.permanently_closed) {
      return <Chip label="Permanently Closed" size="small" color="error" />;
    }
    if (place.temporarily_closed) {
      return <Chip label="Temporarily Closed" size="small" color="warning" />;
    }
    return <Chip label="Open" size="small" color="success" />;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Outlet Management (Google Places)
        </Typography>
        <Button
          variant="contained"
          startIcon={<StoreIcon />}
          onClick={openCreateDialog}
        >
          Add Place
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search title, address..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select
                value={filterBrand}
                onChange={(e) => { setFilterBrand(e.target.value); setPage(1); }}
                label="Brand"
              >
                <MenuItem value="">All Brands</MenuItem>
                {filterOptions.brands.map((b) => (
                  <MenuItem key={b.brand_id} value={b.brand_id}>
                    {b.brand_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>City</InputLabel>
              <Select
                value={filterCity}
                onChange={(e) => { setFilterCity(e.target.value); setPage(1); }}
                label="City"
              >
                <MenuItem value="">All Cities</MenuItem>
                {filterOptions.cities.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="permanently_closed">Permanently Closed</MenuItem>
                <MenuItem value="temporarily_closed">Temporarily Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setFilterBrand('');
                setFilterCity('');
                setFilterStatus('');
                setPage(1);
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Places Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Google URL</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Reviews</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && places.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : places.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No places found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                places.map((place) => (
                  <TableRow key={place.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 200 }} noWrap>
                        {place.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {place.url ? (
                        <Chip
                          icon={<LinkIcon />}
                          label="Maps"
                          size="small"
                          variant="outlined"
                          color="primary"
                          component="a"
                          href={place.url}
                          target="_blank"
                          clickable
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{place.city || '-'}</TableCell>
                    <TableCell>
                      {place.brand_name ? (
                        <Chip label={place.brand_name} size="small" color="primary" variant="outlined" />
                      ) : place.brand_id ? (
                        <Chip label={place.brand_id} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Rating
                          value={place.total_score ? Number(place.total_score) : 0}
                          precision={0.1}
                          size="small"
                          readOnly
                        />
                        <Typography variant="body2" color="text.secondary">
                          {place.total_score ? Number(place.total_score).toFixed(1) : '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{place.reviews_count ?? 0}</TableCell>
                    <TableCell>{getStatusChip(place)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, place)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" py={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuPlace) openViewDialog(menuPlace);
            handleMenuClose();
          }}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuPlace) openEditDialog(menuPlace);
            handleMenuClose();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuPlace) {
              setPlaceToDelete(menuPlace);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit/View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {dialogMode === 'create' ? 'Add Place' : dialogMode === 'edit' ? 'Edit Place' : 'View Place'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  disabled={isReadOnly}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={formData.brand_id}
                    onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                    label="Brand"
                    disabled={isReadOnly}
                    required
                  >
                    {filterOptions.brands.map((b) => (
                      <MenuItem key={b.brand_id} value={b.brand_id}>
                        {b.brand_name} ({b.brand_id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Google Maps URL"
              placeholder="https://www.google.com/maps/place/..."
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              disabled={isReadOnly}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Google Maps URL used by the crawler to scrape reviews"
            />

            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              disabled={isReadOnly}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country Code"
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Google Place ID"
                  value={formData.place_id}
                  onChange={(e) => setFormData({ ...formData, place_id: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Latitude"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  disabled={isReadOnly}
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Longitude"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  disabled={isReadOnly}
                  type="number"
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Category"
              value={formData.category_name}
              onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
              disabled={isReadOnly}
            />

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.permanently_closed}
                      onChange={(e) =>
                        setFormData({ ...formData, permanently_closed: e.target.checked })
                      }
                      disabled={isReadOnly}
                    />
                  }
                  label="Permanently Closed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.temporarily_closed}
                      onChange={(e) =>
                        setFormData({ ...formData, temporarily_closed: e.target.checked })
                      }
                      disabled={isReadOnly}
                    />
                  }
                  label="Temporarily Closed"
                />
              </Grid>
            </Grid>

            {dialogMode === 'view' && selectedPlace && (
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Additional Info
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Rating:</strong> {selectedPlace.total_score ? Number(selectedPlace.total_score).toFixed(1) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Reviews:</strong> {selectedPlace.reviews_count ?? 0}
                    </Typography>
                  </Grid>
                  {selectedPlace.url && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Google URL:</strong>{' '}
                        <a href={selectedPlace.url} target="_blank" rel="noopener noreferrer">
                          {selectedPlace.url}
                        </a>
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Created:</strong> {new Date(selectedPlace.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Updated:</strong> {selectedPlace.updated_at ? new Date(selectedPlace.updated_at).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{placeToDelete?.title}</strong>?
            This will also remove all associated reviews and ratings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutletManagementPage;
